// 百寶袋（vender 商品）資料模型 — 對應「台灣醫智（商家版）」個案端 API
// 欄位採 snake_case 直接對齊後端回傳（同 vender.model.ts / CalendarEventInfo.model.ts 慣例，不做轉換層）。

// 商品規格（平面單選 SKU，各自有售價；無規格商品 skus 為空陣列）
export interface VenderProductSkuInfo {
  product_sku_id: number;
  sku_name: string;
  sku_price: number;
  sku_list_price?: number; // 原價（可選，顯示刪除線）
}

// 商品（列表與詳情共用；product_status / can_order 僅詳情 API 回傳）
export interface VenderProductInfo {
  product_id: number;
  product_name: string;
  product_price: number; // 無規格時以此為準，有規格時以各 sku_price 為準
  product_list_price?: number; // 原價（可選，顯示刪除線）
  product_image_url?: string | null;
  product_images?: string[];
  product_desc?: string | null; // HTML 格式
  product_category?: string | string[] | null; // 商品分類：新版 API 回陣列，舊版為逗號分隔字串
  sort_no?: number;
  skus?: VenderProductSkuInfo[];
  product_status?: 'PUBLISHED' | 'SUSPENDED';
  allow_cod?: boolean; // 是否允許貨到付款
  can_order?: boolean; // false = 暫停銷售，須鎖定下單按鈕
}

// 訂單（登記單）狀態機：
//   NEW ──(商品備妥)──▶ READY_FOR_PICKUP ──(藥局領取)──▶ PICKED_UP(終態)
//   NEW ──(個案取消)──▶ CANCELED_BY_USER(終態)（商家備妥後個案不可取消）
//   NEW / READY_FOR_PICKUP ──(商家取消)──▶ CANCELED_BY_MERCHANT(終態)
export type VenderProductOrderStatus =
  | 'NEW'
  | 'READY_FOR_PICKUP'
  | 'PICKED_UP'
  | 'CANCELED_BY_USER'
  | 'CANCELED_BY_MERCHANT';

// 配送方式：COD=貨到付款（宅配到府） / STORE_PICKUP=到店取貨
export type VenderProductDeliveryMethod = 'COD' | 'STORE_PICKUP';

// 訂單主檔（show_order_list / show_order_detail.order）
export interface VenderProductOrderInfo {
  order_id: number;
  product_id: number;
  vender_id: number; // 後端回傳的實際商家 ID（request 端已改傳 team_id，由後端解析）
  order_status: VenderProductOrderStatus;
  total_amt: string; // 後端回字串（含小數兩位），顯示時以 +value 轉數字
  // ===== 以下快照欄位僅 show_order_list 回傳（下單當下紀錄，不隨商品變動）=====
  item_name?: string | null; // 商品名稱快照
  sku_name?: string | null; // 規格名稱快照；無規格商品為 null
  item_qty?: number | null; // 下單數量快照
  item_price?: string | null; // 下單單價快照
  item_amount?: string | null; // 下單小計快照（item_price × item_qty）
  product_image_url?: string | null; // 商品主圖（取自商品現況，非快照）
  product_images?: string[] | null; // 商品多圖；主圖為 null 時可取第一張顯示
  // =====================================================================
  customer_name?: string | null;
  customer_tel?: string | null;
  pickup_store?: string | null;
  delivery_method?: VenderProductDeliveryMethod | null; // 配送方式（COD=貨到付款 / STORE_PICKUP=到店取貨）
  recipient_address?: string | null; // 貨到付款收件地址
  customer_note?: string | null;
  ready_at?: string | null;
  picked_up_at?: string | null;
  canceled_at?: string | null;
  canceled_by?: string | null; // 取消者 user_id（僅詳情）
  cancel_remark?: string | null;
  auto_cancel_at?: string | null; // NEW 狀態逾期自動取消時間
  created_at: string;
}

// 訂購明細（價格快照，不隨商品改價變動）
export interface VenderProductOrderItemInfo {
  order_item_id: number;
  item_name: string;
  sku_name?: string | null;
  item_qty: number;
  item_price: string;
  item_amount: string;
}

// 訂單詳情（show_order_detail）
export interface VenderProductOrderDetailInfo {
  order: VenderProductOrderInfo;
  order_items: VenderProductOrderItemInfo[];
}

// 訂單通知記錄（show_notification_list）
export interface VenderProductNotifyInfo {
  notification_id: number;
  order_id: number;
  message: string;
  notified_at: string;
}

// 建立訂單參數（create_order；member_id 由 token 推得，不需傳入）
export interface CreateVenderProductOrderInput {
  team_id: number; // App 團隊 ID，後端自動解析對應商家
  product_id: number;
  product_sku_id?: number; // 有規格商品必填
  qty: number;
  customer_name?: string;
  customer_tel?: string;
  pickup_store?: string;
  note?: string;
  delivery_method: VenderProductDeliveryMethod; // 貨到付款 / 到店取貨
  recipient_address?: string;
}

// 使用者端狀態標籤：自己取消即「已取消」；商家取消明確標示。
export const VENDER_PRODUCT_ORDER_STATUS_MAP: Record<
  VenderProductOrderStatus,
  { label: string; color: string }
> = {
  NEW: { label: '待到貨', color: 'warning' },
  READY_FOR_PICKUP: { label: '待領取', color: 'primary' },
  PICKED_UP: { label: '已領取', color: 'success' },
  CANCELED_BY_USER: { label: '已取消', color: 'medium' },
  CANCELED_BY_MERCHANT: { label: '商家取消', color: 'danger' },
};

// 使用者僅能在「待到貨」（商家備妥前）主動取消；備妥後（待領取）不可取消
export const canUserCancel = (status: VenderProductOrderStatus): boolean =>
  status === 'NEW';

// 進行中（未結案）：待到貨 / 待領取；與「可取消」是不同概念，列表排序與篩選用
export const isOrderOngoing = (status: VenderProductOrderStatus): boolean =>
  status === 'NEW' || status === 'READY_FOR_PICKUP';

// 原價刪除線：原價存在且「高於」售價才回傳（否則回 undefined，不顯示刪除線）
// —— 避免原價 = 售價或更低時誤標特價。三個顯示點（商品卡 / 詳情 / 登記 modal）共用此規則。
export const strikeListPrice = (
  listPrice: number | null | undefined,
  price: number
): number | undefined =>
  listPrice != null && listPrice > price ? listPrice : undefined;

// 「未分類」chip 的顯示名稱（無 tag 商品的虛擬分類）
export const UNCATEGORIZED = '未分類';

// 正規化單一商品的分類（相容陣列與逗號分隔字串兩種格式）
export const productCategories = (p: VenderProductInfo): string[] => {
  const raw = p?.product_category;
  const list = Array.isArray(raw) ? raw : (raw || '').split(',');
  return list.map((c) => (c || '').trim()).filter((c) => !!c);
};

// 從商品清單聚合分類（去重，依出現順序）
export const aggregateCategories = (
  products: VenderProductInfo[]
): string[] => {
  const result: string[] = [];
  for (const p of products || []) {
    for (const name of productCategories(p)) {
      if (!result.includes(name)) {
        result.push(name);
      }
    }
  }
  return result;
};

// 分類 chip（含商品數）
export interface CategoryChipInfo {
  name: string;
  count: number;
}

// 彙整分類 chip：數量多→少排序，「未分類」（無 tag 商品）固定最後
export const aggregateCategoryChips = (
  products: VenderProductInfo[]
): CategoryChipInfo[] => {
  const counts = new Map<string, number>();
  let uncategorized = 0;
  for (const p of products || []) {
    const cats = productCategories(p);
    if (!cats.length) {
      uncategorized++;
      continue;
    }
    for (const c of cats) {
      counts.set(c, (counts.get(c) || 0) + 1);
    }
  }
  const chips = [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
  if (uncategorized) {
    chips.push({ name: UNCATEGORIZED, count: uncategorized });
  }
  return chips;
};

// 商品是否屬於某分類；「未分類」比對無 tag 的商品
export const productInCategory = (
  product: VenderProductInfo,
  category: string
): boolean =>
  category === UNCATEGORIZED
    ? productCategories(product).length === 0
    : productCategories(product).includes(category);
